/**
 * Contains methods to sanitize strings to prevent macro injections when writing to a CSV
 * Following OWASP recommendations: https://owasp.org/www-community/attacks/CSV_Injection
 */

/**
 * Applies CSV sanitization with single quote prefix and escaping double quotes
 * Following OWASP recommendations for CSV injection prevention
 * @param input String to sanitize
 * @returns Sanitized string with single quote prefix for dangerous characters and escaped quotes
 */
function sanitizeString(input: string): string {
  // Escape double quotes if they exist in the string
  const escaped = input.replace(/"/g, '""');

  // Check if string starts with dangerous characters
  const startsWithDangerous = /^[=+\-@\t\r\n]/.test(input);

  // Prepend with single quote only if starts with dangerous characters
  // OWASP recommends wrapping cells in double quotes but the library we're using to export CSV files
  // already does this, if we wrap the string here the cells will show the actual double quotes in the spreadsheet
  return startsWithDangerous ? `'${escaped}` : escaped;
}

/**
 * Sanitizes JavaScript objects for CSV export, ensuring all values are properly stringified and sanitized
 * Handles nested objects and arrays by converting them to strings first
 * @param data Array of objects to sanitize
 * @returns Sanitized data array with all values as sanitized strings
 */
export function sanitizeObjectsForCSV(data: any[]): any[] {
  return data.map((row) => {
    const sanitizedRow: any = {};
    for (const [key, value] of Object.entries(row)) {
      // Convert all values to strings first, then sanitize
      const stringValue = value == null ? "" : String(value);
      sanitizedRow[key] = sanitizeString(stringValue);
    }
    return sanitizedRow;
  });
}
